import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface ProvinciaEclesiastica {
  id: string;
  nombre: string;
}

interface UseProvinciasDisponiblesResult {
  provincias: ProvinciaEclesiastica[];
  loading: boolean;
  error: string | null;
  provinciaSeleccionada: string;
  setProvinciaSeleccionada: (id: string) => void;
}

/**
 * Custom hook para cargar provincias eclesiásticas activas.
 * Selecciona automáticamente la primera provincia si no hay selección previa.
 * 
 * @returns Objeto con lista de provincias, estado de carga, error, y provincia seleccionada
 */
export function useProvinciasDisponibles(): UseProvinciasDisponiblesResult {
  const [provincias, setProvincias] = useState<ProvinciaEclesiastica[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [provinciaSeleccionada, setProvinciaSeleccionada] = useState<string>('');

  useEffect(() => {
    loadProvincias();
  }, []);

  const loadProvincias = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from('provincias_eclesiasticas')
        .select('id, nombre')
        .eq('activo', true)
        .order('nombre');

      if (queryError) throw queryError;

      setProvincias(data || []);

      // Seleccionar automáticamente la primera provincia si no hay selección previa
      if (data && data.length > 0 && !provinciaSeleccionada) {
        setProvinciaSeleccionada(data[0].id);
      }
    } catch (err) {
      console.error('Error loading provincias:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar provincias');
    } finally {
      setLoading(false);
    }
  };

  return {
    provincias,
    loading,
    error,
    provinciaSeleccionada,
    setProvinciaSeleccionada,
  };
}
